import 'primevue/resources/primevue.min.css';
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import 'font-awesome/css/font-awesome.css';
import '@fullcalendar/core';
import '@fullcalendar/daygrid';
import '@fullcalendar/timegrid';
import '@fullcalendar/interaction';
import 'prismjs/themes/prism-coy.css';
import moment from 'moment';

import { createApp, h, reactive } from 'vue';
import VueExcelXlsx from 'vue-excel-xlsx';
import i18n from './plugins/i18n';
import IdleVue from 'idle-vue-3';
import router from './router';
import store from './store';
import axios from 'axios';
import App from './App.vue';
import PrimeVue from 'primevue/config';
import * as PrimeVueConfig from './primevue';
import Sso from './pages/Sso';
import Login from './pages/Login';
import '../node_modules/ag-grid-community/dist/styles/ag-grid.css';
import '../node_modules/ag-grid-community/dist/styles/ag-theme-alpine.css';
import { LicenseManager } from 'ag-grid-enterprise';
import LandingPage from '@/pages/LandingPage';
import { AgGridVue } from 'ag-grid-vue3';
import ExportService from '@/service/ExportService';
import ProfileService from '@/service/UserPreferenceService';
import VueCookies from 'vue-cookies';

// --- KEYCLOAK INTEGRATION START ---
import { getKeycloak, initKeycloak, logout as keycloakLogout } from './service/KeycloakService';
// --- KEYCLOAK INTEGRATION END ---

//import UserProfileService from '@/service/UserProfileService';

/* =============================================
   RUNTIME CONFIG LOADER
   Loads /app-config.json via fetch BEFORE Vue app creation.
============================================= */
async function loadRuntimeConfig() {
  const runtimeConfigCacheKey = 'selectio.runtime-config';
  const cachedConfig = sessionStorage.getItem(runtimeConfigCacheKey);
  if (cachedConfig) {
    try {
      const parsed = JSON.parse(cachedConfig);
      if (typeof parsed === 'object' && parsed !== null) {
        window.__APP_CONFIG__ = parsed;
        return;
      }
    } catch (e) {
      sessionStorage.removeItem(runtimeConfigCacheKey);
    }
  }

  try {
    const res = await fetch('/Selectio/app-config.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error('config not ok');
    const json = await res.json();
    if (typeof json !== 'object' || json === null) throw new Error('invalid json');
    window.__APP_CONFIG__ = json;
    sessionStorage.setItem(runtimeConfigCacheKey, JSON.stringify(json));
  } catch (e) {
    // Fallback minimal config
    window.__APP_CONFIG__ = {
      selectio_cofast_url: '',
      selectio_lens_url: '',
      nettuno_url: '',
      idp: {},
      keycloak: {
        url: '',
        realm: '',
        clientId: '',
      },
    }
  }
}

// Will be assigned inside bootstrap()
let app = null;
const PENDING_AUTH_ROUTE_STORAGE_KEY = 'selectio.pending-auth-route';

function resolveMappedIdpUrl(idp) {
  const idpConfig = window.__APP_CONFIG__?.idp;
  if (typeof idpConfig !== 'object' || idpConfig === null) {
    return '';
  }

  const directMatch = idpConfig[idp];
  if (typeof directMatch === 'string' && directMatch.trim() !== '') {
    return directMatch.trim();
  }

  const normalizedIdp = idp.toLowerCase();
  const matchingKey = Object.keys(idpConfig).find((key) => key.toLowerCase() === normalizedIdp);
  if (!matchingKey) {
    return '';
  }

  const mappedValue = idpConfig[matchingKey];
  return (typeof mappedValue === 'string') ? mappedValue.trim() : '';
}

function redirectToMappedIdpIfPresent() {
  const params = new URLSearchParams(window.location.search);
  const idp = (params.get('idp') || '').trim();
  if (!idp) {
    return false;
  }

  const targetUrl = resolveMappedIdpUrl(idp);
  if (!targetUrl) {
    console.warn(`[IDP] Nessun mapping trovato in app-config.json per idp='${idp}'`);
    return false;
  }

  window.location.replace(targetUrl);
  return true;
}

function shouldApplyBootstrapRedirect(currentPath) {
  if (typeof currentPath !== 'string' || currentPath.trim() === '') {
    return true;
  }

  // Preserve deep-link/current page on full refresh (F5).
  return currentPath === '/' || currentPath === '/landing-page';
}

function hasHydratedUserInStore() {
  const user = store.state?.user;
  return Boolean(
    user &&
    typeof user.username === 'string' &&
    user.username.length > 0 &&
    typeof user.role === 'string' &&
    user.role.length > 0,
  );
}

function consumePendingAuthRouteFromSession() {
  const storedRoute = (sessionStorage.getItem(PENDING_AUTH_ROUTE_STORAGE_KEY) || '').trim();
  sessionStorage.removeItem(PENDING_AUTH_ROUTE_STORAGE_KEY);

  if (!storedRoute || storedRoute === '/' || storedRoute === '/landing-page') {
    return '';
  }
  if (!storedRoute.startsWith('/')) {
    return '';
  }
  return storedRoute;
}

/* ===============================
   Helper utilities (unchanged)
=============================== */
function getPortfolioListError(store, vueInstance) {
  vueInstance.$toast.add({
    severity: 'error',
    summary: vueInstance.$t('common.toasts.userNotConfigured.summary'),
    detail: vueInstance.$t('common.toasts.userNotConfigured.detail'),
    life: 6000,
  });
  store.dispatch('stopLoading');
}

/* ===============================
   BOOTSTRAP: create and mount app
=============================== */
async function bootstrap() {
  // 1. Carica la configurazione (endpoint API, ecc.)
  await loadRuntimeConfig();

  // 2. Inizializza Keycloak. L'app Vue parte solo se l'utente è autenticato.
  if (redirectToMappedIdpIfPresent()) {
    return;
  }

  initKeycloak(async () => {

    app = createApp({
      computed: {
        ViewComponent() {
          switch (this.$route.path) {
            case '/sso':
              return Sso;
            case '/login':
              // Con Keycloak il login interno non serve più, ma lo lasciamo per fallback
              return Login;
            case '/landing-page':
              return LandingPage;
            default:
              return App;
          }
        },
      },
      render() {
        return h(this.ViewComponent);
      },
    });

    // Make runtime config available app-wide (no store)
    app.config.globalProperties.$config = window.__APP_CONFIG__;

    app.config.globalProperties.$appState = reactive({ inputStyle: 'outlined' });

    LicenseManager.setLicenseKey('CompanyName=COMPAGNIE FRANCAISE DASSURANCE POUR LE COMMERCE EXTERIEUR S,LicensedGroup=IT,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=2,AssetReference=AG-016707,ExpiryDate=31_July_2022_[v2]_MTY1OTIyMjAwMDAwMA==a938ad1e40fecdb61aa3232b62c69bbb');

    app.use(PrimeVue, { ripple: true });
    app.use(router);
    app.use(store);
    app.use(i18n);
    app.use(VueCookies);
    app.use(AgGridVue);
    app.use(VueExcelXlsx);
    app.use(IdleVue, { idleTime: 600000, store });

    // Endpoint logout frontend
    router.addRoute({
      path: '/logout',
      beforeEnter() {
        // Rimuove lo stato salvato
        localStorage.removeItem('vuex');
        keycloakLogout();
      },
    });

    app.config.globalProperties.$toolips = {
      standard(text) {
        return {
          value: text,
          pt: {
            arrow: {
              style: { borderBottomColor: 'var(--primary-color)' },
            },
            text: 'bg-primary font-medium',
          },
        };
      },
    };

    app.config.globalProperties.$xlsx = {
      excelBuyerList(listToDownload, fileName, langOrColumnNames, vueInstance) {

        if (isEmpty(listToDownload)) {
          vueInstance.$toast.add({
            severity: vueInstance.$t('common.toasts.emptyListToDownload.severity'),
            summary: vueInstance.$t('common.toasts.emptyListToDownload.summary'),
            detail: vueInstance.$t('common.toasts.emptyListToDownload.detail'),
            life: 5000,
          });
        } else {

          const exportService = new ExportService();
          vueInstance.$store.dispatch('startLoading');

          const payload = {
            headerList: createColumns(listToDownload[0], langOrColumnNames, vueInstance).map(column => {
              if (column.startsWith("buyers.analysis.columns.")) {
                column = column.replace("buyers.analysis.columns.", "");
              }
              return column
            }),
            objectsToBeExported: [],
          };

          listToDownload.forEach(item => {
            const list = Object.values(item);
            payload.objectsToBeExported.push(list);
          });

          exportService.excelBasic(payload).then(response => {
            const link = window.document.createElement('a');
            link.href = 'data:application/xlsx;base64,' + response.result;
            link.download = fileName + '.xlsx';
            link.click();
            vueInstance.$store.dispatch('stopLoading');
          }).catch(() => {
            vueInstance.$store.dispatch('stopLoading');
            vueInstance.$toast.add({
              severity: 'error',
              summary: vueInstance.$t('toasts.somethingWentWrong.summary'),
              detail: vueInstance.$t('toasts.somethingWentWrong.detail'),
              life: 5000,
            });
          });
        }
      },
      excel(listToDownloadInput, filtersInput, listOfFieldTypeData,
            listOfFieldToDelete, fileName, langOrColumnNames, vueInstance) {

        if (isEmpty(listToDownloadInput)) {
          vueInstance.$toast.add({
            severity: vueInstance.$t('common.toasts.emptyListToDownload.severity'),
            summary: vueInstance.$t('common.toasts.emptyListToDownload.summary'),
            detail: vueInstance.$t('common.toasts.emptyListToDownload.detail'),
            life: 5000,
          });
        } else {

          const exportService = new ExportService();
          vueInstance.$store.dispatch('startLoading');

          let listToDownload = deepClone(listToDownloadInput);
          let filters = deepClone(filtersInput);

          listOfFieldTypeData.forEach(key => {
            filters[key].value = filters[key].value !== null
            && typeof filters[key].value !== 'undefined' ? formatDate(
              new Date(filters[key].value),
              vueInstance.$t('common.dateFormat')) : '';
          });

          listToDownload.forEach(item => {
            listOfFieldTypeData.forEach(key => {
              item[key] = item[key] !== null && typeof item[key]
              !== 'undefined' ? formatDate(new Date(item[key]),
                vueInstance.$t('common.dateFormat')) : '';
            });
          });

          if (!isEmpty(filters)) {
            listToDownload = listToDownloadFiltered(listToDownload, filters,
              vueInstance);
          }

          // CORREZIONE QUI: Itera sulla lista per cancellare il campo da ogni item
          if (!isEmpty(listOfFieldToDelete)) {
            listOfFieldToDelete.forEach(fieldToDelete => {
              listToDownload.forEach(item => {
                delete item[fieldToDelete];
              });
            });
          }

          const payload = {
            headerList: createColumns(listToDownload[0], langOrColumnNames, vueInstance),
            objectsToBeExported: [],
          };

          listToDownload.forEach(item => {
            const list = Object.values(item);
            payload.objectsToBeExported.push(list);
          });

          exportService.excelBasic(payload).then(response => {
            const link = window.document.createElement('a');
            link.href = 'data:application/xlsx;base64,' + response.result;
            link.download = fileName + '.xlsx';
            link.click();
            vueInstance.$store.dispatch('stopLoading');
          }).catch(() => {
            vueInstance.$store.dispatch('stopLoading');
            vueInstance.$toast.add({
              severity: 'error',
              summary: vueInstance.$t('toasts.somethingWentWrong.summary'),
              detail: vueInstance.$t('toasts.somethingWentWrong.detail'),
              life: 5000,
            });
          });
        }
      },
    };

    app.config.globalProperties.$endpoints = {
      getPortfolioList(user, router, store, vueInstance) {
        const userPreferenceService = new ProfileService();
        if (user.role === 'selectiodir') {
          if (!isEmpty(user.username) && !isEmpty(user.companyCode)) {
            store.dispatch('startLoading');
            userPreferenceService.searchUserPortfolios([
                { fieldId: 'username', match: 'eq', value: user.username },
                { fieldId: 'companyId', match: 'eq', value: user.companyCode },
              ])
              .then(response => {
                user.portfolioList = response;
                store.dispatch('login', { vm: { vm: vueInstance }, user: user });
                store.dispatch('stopLoading');
                router.push('/');
              });
          } else
            getPortfolioListError(store, vueInstance);
        }
        if (user.role === 'selectiocli' || user.role === 'selectiobro') {
          if (!isEmpty(user.customerCode) && !isEmpty(user.companyCode)) {
            store.dispatch('startLoading');
            userPreferenceService.searchUserPortfolios([
                { fieldId: 'customerCode', match: 'eq', value: user.customerCode },
                { fieldId: 'username', match: 'eq', value: user.username },
                { fieldId: 'companyId', match: 'eq', value: user.companyCode },
              ])
              .then(response => {
                user.portfolioList = response;
                store.dispatch('login', { vm: { vm: vueInstance }, user: user });
                store.dispatch('stopLoading');
                router.push('/');
              });
          } else
            getPortfolioListError(store, vueInstance);
        }
        if (user.role === 'selectio-capo-gruppo') {
          if (!isEmpty(user.customerCode) && !isEmpty(user.companyCode)) {
            store.dispatch('startLoading');
            userPreferenceService.searchUserPortfolios([
                { fieldId: 'groupCode', match: 'eq', value: user.customerCode },
                { fieldId: 'username', match: 'eq', value: user.username },
                { fieldId: 'companyId', match: 'eq', value: user.companyCode },
              ])
              .then(response => {
                user.portfolioList = response;
                store.dispatch('login', { vm: { vm: vueInstance }, user: user });
                store.dispatch('stopLoading');
                router.push('/');
              });
          } else
            getPortfolioListError(store, vueInstance);
        }
        if (user.role === 'selectioage') {
          if (!isEmpty(user.agencyCode) && !isEmpty(user.companyCode)) {
            store.dispatch('startLoading');
            userPreferenceService.searchUserPortfolios([
                { fieldId: 'agencyCode', match: 'eq', value: user.agencyCode },
                { fieldId: 'username', match: 'eq', value: user.username },
                { fieldId: 'companyId', match: 'eq', value: user.companyCode },
              ])
              .then(response => {
                user.portfolioList = response;
                store.dispatch('login', { vm: { vm: vueInstance }, user: user });
                store.dispatch('stopLoading');
                router.push('/');
              });
          } else
            getPortfolioListError(store, vueInstance);
        }
        return [];
      },

      goToSelectio(user, router, store, vueInstance, role) {
        store.dispatch('startLoading');
        const selectioRoles = Array.isArray(user.roles)
          ? user.roles.filter((roleCode) => typeof roleCode === 'string' && roleCode.startsWith('selectio'))
          : [];
        user.roles = selectioRoles;

        const resolvedRole = !isEmpty(role)
          ? role
          : ((typeof user.role === 'string' && user.role.startsWith('selectio'))
            ? user.role
            : selectioRoles[0]);
        user.role = resolvedRole;
        store.dispatch('stopLoading');
        if (Array.isArray(user.portfolioList) && user.portfolioList.length > 0) {
          store.dispatch('login', { vm: { vm: vueInstance }, user: user });
          router.push('/');
        } else {
          this.getPortfolioList(user, router, store, vueInstance);
        }
      },

      goToCofast() {
        const target = window.__APP_CONFIG__?.selectio_cofast_url || '';
        window.open(`${target}`, '_self');
      },
      goToLens() {
        const target = window.__APP_CONFIG__?.selectio_lens_url || '';
        window.open(`${target}`, '_self');
      },
      goToNettuno() {
        const target = window.__APP_CONFIG__?.nettuno_url || '';
        window.open(`${target}`, '_self');
      },
    };

    app.config.globalProperties.$methods = {
      toNumber(value) {
        return isNaN(value) ? 0 : Number(value);
      },
      formatDate(value, format) {
        return formatDate(value, format);
      },
      sendToast(vue, errorKey) {
        if (errorKey != null) {
          vue.$toast.add({
            severity: vue.$t('common.toasts.' + errorKey + '.severity'),
            summary: vue.$t('common.toasts.' + errorKey + '.summary'),
            detail: vue.$t('common.toasts.' + errorKey + '.detail'),
            life: 6000,
          });
        }
      },
      deepClone(object) {
        return deepClone(object);
      },
      isEmpty(object) {
        return isEmpty(object);
      },
      isDuplicatedField(values, field) {
        return isDuplicatedField(values, field);
      },

      //#region Metodi ACL
      aclIsEditable(elementId) {
        const rule = store.state.acl.aclRules.find(aclRule => aclRule.elementId === elementId);
        return rule ? rule.editable : true;
      },
      aclIsVisible(elementId) {
        const rule = store.state.acl.aclRules.find(aclRule => aclRule.elementId === elementId);
        return rule ? rule.visible : true;
      },
      aclIsClickable(elementId) {
        const rule = store.state.acl.aclRules.find(aclRule => aclRule.elementId === elementId);
        return rule ? rule.clickable : true;
      },
      //#endregion
    };

    PrimeVueConfig.init(app);

    await router.isReady();

    // --- KEYCLOAK: CARICAMENTO PROFILO UTENTE ---
    // Prima di montare l'app, carichiamo i dati utente da IAM usando il token
    try {
      if (hasHydratedUserInStore()) {
        const keycloakToken = getKeycloak().token;
        if (keycloakToken) {
          store.commit('setToken', keycloakToken);
        }
        await store.dispatch('acl/createAclRules', { vm: null, user: store.state.user });
      } else {
        await store.dispatch('loadUserFromIam');
      }
      const user = store.getters['user'];
      if (user) {
        let currentPath = router.currentRoute.value?.path || '/';
        if (currentPath === '/') {
          const restorableRoute = consumePendingAuthRouteFromSession();
          if (restorableRoute) {
            try {
              await router.replace(restorableRoute);
              currentPath = router.currentRoute.value?.path || '/';
            } catch (e) {
              console.warn('[Routing] Impossibile ripristinare ultima route:', restorableRoute, e);
            }
          }
        }
        const shouldApplyDefaultRedirect = shouldApplyBootstrapRedirect(currentPath);
        let enabledCount = 0;
        if (user.selectioEnabled) enabledCount++;
        if (user.cofastEnabled) enabledCount++;
        if (user.lensEnabled) enabledCount++;
        if (user.databrainEnabled) enabledCount++;

        if (enabledCount > 1) {
          if (shouldApplyDefaultRedirect && currentPath !== '/landing-page') {
            router.replace('/landing-page');
          }
        } else if (enabledCount === 1 && user.selectioEnabled) {
          if (shouldApplyDefaultRedirect && currentPath !== '/') {
            router.replace('/');
          }
        } else if (enabledCount === 1 && user.cofastEnabled) {
          app.config.globalProperties.$endpoints.goToCofast();
        } else if (enabledCount === 1 && user.lensEnabled) {
          app.config.globalProperties.$endpoints.goToLens();
        } else if (enabledCount === 1 && user.databrainEnabled) {
          app.config.globalProperties.$endpoints.goToNettuno();
        }
      }
    } catch (e) {
      console.error("Impossibile caricare il profilo utente da IAM", e);
    }

    app.mount('#app');
  }); // End initKeycloak wrapper
}

bootstrap();

/* ===============================
   Exports & helpers (remain top-level)
=============================== */
export function getSelectioAxios() {
  axios.defaults.baseURL = '/Selectio/api';
  setAxiosHeaders(axios);
  if (getUser() != null && getUser().token != null) {
    axios.defaults.headers.common.authorization = `Bearer ${getUser() != null ? getUser().token : null}`;
  }
  return axios;
}

export function integerFormat(value) {
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function amountFormat(value) {
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function isValidEmail(value){
  console.log("value:", value)
  return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,50})+$/.test(value);
}

export function percentageFormatSuffix(value) {
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value) + ' %';
}


function setAxiosHeaders(axios) {
  axios.defaults.headers.common = {
    // Prefer runtime config, fallback to store getter if present
    'x-tars-endpoint': `${window.__APP_CONFIG__?.tarsUrl || getTarsEndpoint() || ''}`,
    'Accept-Language': `${getBrowserLanguage()}`,
  };
}

export function setUser(user) {
  store.commit('setUser', user);
}

export function getUser() {
  if (app != null && store != null && store.state) {
    return store.state.user;
  }
}

export function getEditing() {
  if (app != null && store != null && store.state) {
    return store.state.editing;
  }
  return false;
}

export function getShowDialogEditManager() {
  if (app != null && store != null && store.state) {
    return store.state.showDialogEditManager;
  }
  return false;
}

export function openDialogEditManager(from) {
  if (app != null && store != null && store.state) {
    store.commit('setShowDialogEditManager', true);
    store.commit('setFromRoute', from);
  }
}

export function getTarsEndpoint() {
  if (app != null && store != null && store.state) {
    return store.state.tarsEndpoint;
  }
}

export function getBrowserLanguage() {
  if (app != null && i18n != null) {
    return i18n.global.tc('languageCode');
  }
}

export function formatDate(value, format) {
  if (!isEmpty(value) && !isEmpty(format)) {
    return moment(value instanceof Date ? value.toISOString() : new Date(value).toISOString()).format(format.toUpperCase());
  }
}

export function deepClone(object) {
  if (object != null) {
    return JSON.parse(JSON.stringify(object));
  }
}

export function isEmpty(object) {
  return (object == null || object.length === 0 || object === '' || typeof object === 'undefined');
}

export function isDuplicatedField(values, field) {
  const valueArr = values.map(item => item[field]);
  return valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
}

export function filterValueManager(key, filters) {
  if (!isEmpty(filters[key].value) && typeof filters[key].value
    === 'string') {
    return filters[key].value.toUpperCase();
  } else {
    return filters[key].value;
  }
}

export function listToDownloadFiltered(listToDownload, filtersInput) {
  return listToDownload.filter(item => {
    const filters = Object.keys(filtersInput).map(key => ({
      key,
      value: filterValueManager(key, filtersInput),
    }));
    const conditions = filters.map(filter => {
      if (filter.value && typeof filter.value === 'string') {
        return !isEmpty(item[filter.key]) ? String(
          item[filter.key]).toUpperCase().includes(filter.value) : '';
      }
      if (filter.value && typeof filter.value === 'number') {
        return !isEmpty(item[filter.key]) ? item[filter.key]
          === filter.value : '';
      }
      return true;
    });
    return conditions.every(condition => condition);
  });
}

export function createColumns(obj, lang, vueInstance) {
  if (Array.isArray(lang) && lang.length > 0)
    return lang;
  const fields = Object.getOwnPropertyNames(obj);
  let columnsList = [];
  fields.forEach(element => columnsList.push(vueInstance.$t(lang + element)));
  return columnsList;
}

export function translateGridRows(rows, field, $translator) {
  rows.forEach(row => {
    row[field] = $translator(row[field]);
  });
  return rows;
}

export function d(...args) {
  const processedArgs = args.map(arg => {
    if (typeof arg === 'object' || typeof arg === 'function') {
      try {
        return JSON.parse(JSON.stringify(arg));
      } catch (e) {
        return '[Unserializable object]';
      }
    }
    return arg;
  });

  console.log(...processedArgs);
}

window.d = d;

export function tempRemap(buyer) {
  Object.keys(buyer)
    .filter(k => k.startsWith('byr'))
    .forEach(k => {
      const newK = k.substring(3, 4).toLowerCase() + k.substring(4);
      buyer[newK] = buyer[k];
      delete buyer[k];
    });
  return buyer;
}

export function range(min, max) {
  const res = [];
  for (let i = min; i <= max; i++)
    res.push(i);
  return res;
}

export function statusDescription(status) {
  if (status != null) {
    return this.$t('portfolio.status.' + status);
  } else
    return '';
}

export function severityStatus(status) {
  if (status === 'A') {
    return 'success';
  }
  if (status === 'T') {
    return 'warning';
  }
  if (status === 'D') {
    return 'danger';
  }
  return '';
}

export function hasChangedValues(portfolio, period, checkData) {
  // Verifica che il portfolio sia valido
  if (!portfolio || !portfolio.id) {
    return false;
  }

  // Verifica che il periodo sia valido
  if (!period || !period.id) {
    return false;
  }

  // Confronta con i dati salvati nell'analisi
  return period.id !== checkData.idPeriod ||
    portfolio.id !== checkData.idPortfolio;
}

export class Profiler {
  constructor() {
    this.lastTime = performance.now();
  }

  step(label) {
    const now = performance.now();
    const elapsed = (now - this.lastTime);
    console.log(`${label}: ${elapsed.toFixed(3)} s`);
    this.lastTime = now;
  }
}

export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function dateToExcelSerial(date) {
  if (!(date instanceof Date))
    date = new Date(date);

  // Excel usa come epoch il 1 gennaio 1900
  const excelEpoch = new Date(1900, 0, 1);

  // Calcola la differenza in millisecondi
  const diff = date - excelEpoch;

  // Converti in giorni (millisecondi -> giorni)
  const days = diff / (1000 * 60 * 60 * 24);

  // console.log(`date=${date} epoch=${excelEpoch} diff=${diff} days=${days}`);

  // Excel ha un bug: considera il 1900 come bisestile
  // quindi aggiungiamo 2 giorni (1 per compensare il bug + 1 per l'offset)
  return days + 2;
}

// src/services/fileService.js

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // Legge il file come Data URL (che è il formato Base64)
    reader.readAsDataURL(file);

    // Gestione del successo
    reader.onload = () => resolve(reader.result);

    // Gestione dell'errore
    reader.onerror = (error) => reject(error);
  });
};
